/**
 * The environment label on every analytics event, derived from the backend we actually talk to
 * (ENG-2810).
 *
 * It used to be `process.env.REACT_APP_API_BASE_URL ? "dev" : "prod"` — i.e. inferred from whether an
 * override had been passed at build time, on the stated assumption that "production hits the default
 * prod backend (no override)". `scripts/deploy-all.sh` breaks that assumption: it exports the override
 * for EVERY environment, and `get_backend_url("production")` returns the very same URL as the fallback
 * below. A prod build through that path therefore sent its traffic to prod and labelled it `dev`.
 * (Checked before changing it: prod BigQuery shows `platform="account"` events as `env=prod` only, so
 * the live build came from `yarn deploy` and the trap had not fired. It was still one deploy away.)
 *
 * Reading the resolved host instead means the label cannot disagree with where the data went, whoever
 * built it and however.
 *
 * ⚠ This introduces two values the events table has not seen before: `alpha` and `local`. Previously
 * alpha and dev were both reported as `dev`, so the two could not be told apart at all. Existing rows
 * are unchanged, and `env` is not bound by any dashboard filter today — but if you are comparing across
 * the cutover, alpha traffic moves out of `dev`.
 */
export function resolveAnalyticsEnv(baseUrl: string): string {
    let host: string;
    try {
        host = new URL(baseUrl).hostname;
    } catch {
        return "prod"; // a malformed override must not silently retag production
    }
    if (host === "localhost" || host === "127.0.0.1" || host === "[::1]") return "local";
    // Order matters: the prod host is a prefix of nothing, but every non-prod host starts with the
    // prod host's name, so the specific ones have to be tested first.
    if (host.startsWith("trusty-backend-alpha")) return "alpha";
    if (host.startsWith("trusty-backend-dev")) return "dev";
    return "prod";
}
